import "../onboarding.css";
import { store } from "../state";
import { navigate } from "../router";
import { markFirstRun } from "../ui/entrance";
import { getDailyHeadline } from "../copy/engine";
import { dailyReminder } from "../copy/reminders";
import { DEFAULT_HOUR, enableReminders, hourLabel, notificationReadiness, pushServerKey, requestNotificationPermission } from "../notify";

/**
 * First-run setup, one question per screen: event, person, start date, end
 * date, then an optional daily reminder (skipped where the browser can't do
 * notifications at all). How rude it gets is the dial on the countdown screen.
 * Each step validates on its own, Enter moves on, and the draft survives a
 * reload (sessionStorage) so a phone call mid-setup doesn't lose it.
 */

interface Draft {
  /** Picked up front so the greeting preview matches what day one shows. */
  id: string;
  name: string;
  personName: string;
  /** YYYY-MM-DD */
  start: string;
  /** YYYY-MM-DD */
  end: string;
  /** HH:MM */
  endTime: string;
  /** Reminder hour, 0-23. */
  hour: number;
}

type StepId = "intro" | "event" | "person" | "start" | "end" | "notify";
const ALL_STEPS: StepId[] = ["intro", "event", "person", "start", "end", "notify"];
const SHARE_SVG = `<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 15V3M8 7l4-4 4 4"/><path d="M6 11H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-1"/></svg>`;
const DRAFT_KEY = "cuntdown:onboarding";

const BACK_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>`;

const EVENT_IDEAS = ["Stag do", "Holiday", "Last day at work", "Flight home", "Wedding", "Big birthday"];

const pad = (n: number) => String(n).padStart(2, "0");
const toDateValue = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmt = (n: number) => n.toLocaleString("en-GB");

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

function startChips(today: Date): { label: string; value: string }[] {
  const chips = [
    { label: "Today", value: toDateValue(today) },
    { label: "Yesterday", value: toDateValue(addDays(today, -1)) },
  ];
  const sinceMonday = (today.getDay() + 6) % 7 || 7;
  chips.push({ label: "Last Monday", value: toDateValue(addDays(today, -sinceMonday)) });
  if (today.getDate() > 2) chips.push({ label: "1st of the month", value: toDateValue(new Date(today.getFullYear(), today.getMonth(), 1)) });
  return chips;
}

function endChips(today: Date): { label: string; value: string }[] {
  const nextOf = (month: number, day: number) => {
    const d = new Date(today.getFullYear(), month, day);
    return d <= today ? new Date(today.getFullYear() + 1, month, day) : d;
  };
  const inAMonth = new Date(today);
  inAMonth.setMonth(inAMonth.getMonth() + 1);
  return [
    { label: "In a week", value: toDateValue(addDays(today, 7)) },
    { label: "In a month", value: toDateValue(inAMonth) },
    { label: "Christmas", value: toDateValue(nextOf(11, 25)) },
    { label: "New Year's Eve", value: toDateValue(nextOf(11, 31)) },
  ];
}

/** The moment the countdown starts from: now if the start is today, otherwise that morning. */
function startInstant(draft: Draft): Date {
  const today = toDateValue(new Date());
  return draft.start === today ? new Date() : new Date(`${draft.start}T00:00`);
}

function endInstant(draft: Draft): Date | null {
  if (!draft.end) return null;
  const d = new Date(`${draft.end}T${draft.endTime || "09:00"}`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function loadDraft(): Draft {
  const today = new Date();
  const fallback: Draft = {
    id: crypto.randomUUID(),
    name: "",
    personName: "",
    start: toDateValue(today),
    end: "",
    endTime: "09:00",
    hour: DEFAULT_HOUR,
  };
  try {
    const saved = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "null");
    return saved ? { ...fallback, ...saved } : fallback;
  } catch {
    return fallback;
  }
}

function saveDraft(draft: Draft, step: StepId) {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ ...draft, step }));
  } catch {
    // private mode: the draft just won't survive a reload
  }
}

function savedStep(steps: StepId[]): StepId {
  try {
    const step = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "null")?.step;
    return steps.includes(step) ? step : "intro";
  } catch {
    return "intro";
  }
}

export function renderOnboarding(root: HTMLElement): () => void {
  const draft = loadDraft();
  // The reminder step only shows where notifications can work, or could once
  // the app is on the iPhone Home Screen.
  const readiness = notificationReadiness();
  const STEPS = readiness === "unsupported" ? ALL_STEPS.filter((s) => s !== "notify") : ALL_STEPS;
  /** The questions that count towards the progress bar. */
  const QUESTIONS: StepId[] = STEPS.filter((s) => s !== "intro");
  const lastStep = STEPS[STEPS.length - 1];
  let step: StepId = savedStep(STEPS);
  // Fetched now so the permission prompt isn't kept waiting on the network.
  if (readiness === "default" || readiness === "granted") void pushServerKey();
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  root.innerHTML = `
    <div class="onb subscreen">
      <div class="onb-top">
        <button type="button" class="back-btn onb-back" data-el="back" aria-label="Back">${BACK_SVG}</button>
        <div class="onb-progress" data-el="progress" aria-hidden="true">${QUESTIONS.map(() => "<i></i>").join("")}</div>
        <div class="onb-count" data-el="count"></div>
      </div>
      <form class="onb-stage" data-el="stage" novalidate></form>
      <div class="onb-foot">
        <button type="button" class="btn btn-primary onb-next" data-el="next"></button>
        <button type="button" class="onb-skip" data-el="skip"></button>
      </div>
    </div>`;

  const q = <T extends HTMLElement = HTMLElement>(name: string) => root.querySelector<T>(`[data-el="${name}"]`)!;
  const stage = q<HTMLFormElement>("stage");
  const backBtn = q<HTMLButtonElement>("back");
  const nextBtn = q<HTMLButtonElement>("next");
  const skipBtn = q<HTMLButtonElement>("skip");
  const progress = q("progress");
  const count = q("count");

  // ---------- per-step markup ----------

  function chipRow(chips: { label: string; value: string }[], current: string, field: string): string {
    return `<div class="onb-chips" role="group" aria-label="Suggestions">${chips
      .map(
        (c) =>
          `<button type="button" class="onb-chip ${c.value === current ? "is-on" : ""}" data-chip-field="${field}" data-chip-value="${escapeHtml(c.value)}">${escapeHtml(c.label)}</button>`
      )
      .join("")}</div>`;
  }

  function stepHTML(): string {
    const today = new Date();
    switch (step) {
      case "intro":
        return `
          <div class="onb-hero" aria-hidden="true">
            <span class="onb-logo"><span></span></span>
            <span class="onb-hero-word">C<b>*</b>UNTDOWN</span>
          </div>
          <h1 class="onb-q">One vulgar countdown to one thing that matters.</h1>
          <p class="onb-hint">A few quick questions and you're counting. Takes about twenty seconds.</p>`;
      case "event":
        return `
          <label class="onb-q" for="onb-input">What are we counting down to?</label>
          <p class="onb-hint">Keep it short. It goes on the ticket.</p>
          <input id="onb-input" class="onb-input" name="name" type="text" maxlength="60" autocomplete="off" enterkeyhint="next" placeholder="Stag do" value="${escapeHtml(draft.name)}" />
          ${chipRow(EVENT_IDEAS.map((e) => ({ label: e, value: e })), draft.name, "name")}`;
      case "person":
        return `
          <label class="onb-q" for="onb-input">Who's it for?</label>
          <p class="onb-hint">Or who's dragging you into it. We'll say hello to them every morning.</p>
          <input id="onb-input" class="onb-input" name="personName" type="text" maxlength="60" autocomplete="given-name" enterkeyhint="next" placeholder="Dan" value="${escapeHtml(draft.personName)}" />
          <div class="onb-preview"><span class="onb-preview-tag">Preview</span><span data-el="greeting"></span></div>`;
      case "start":
        return `
          <label class="onb-q" for="onb-input">When did the wait start?</label>
          <p class="onb-hint">It fills the progress bar. Most people pick today.</p>
          <input id="onb-input" class="onb-input" name="start" type="date" max="${toDateValue(today)}" value="${draft.start}" />
          ${chipRow(startChips(today), draft.start, "start")}`;
      case "end":
        return `
          <label class="onb-q" for="onb-input">And when's the big day?</label>
          <p class="onb-hint">Has to be in the future. That's the whole app.</p>
          <div class="onb-date-row">
            <input id="onb-input" class="onb-input" name="end" type="date" min="${toDateValue(today)}" value="${draft.end}" />
            <input class="onb-input onb-time" name="endTime" type="time" aria-label="Time" value="${draft.endTime}" />
          </div>
          ${chipRow(endChips(today), draft.end, "end")}
          <div class="onb-readout" data-el="readout" aria-live="polite"></div>`;
      case "notify":
        return notifyHTML();
    }
  }

  function reminderPreview(): string {
    const end = endInstant(draft);
    if (!end) return "";
    const msg = dailyReminder(
      { id: draft.id, name: draft.name.trim(), personName: draft.personName.trim(), targetDate: end.toISOString() },
      store.get().censorEnabled
    );
    return `
      <div class="onb-notif" aria-label="Example reminder">
        <span class="onb-notif-icon" aria-hidden="true"><span></span></span>
        <span class="onb-notif-text">
          <span class="onb-notif-head"><b>C*UNTDOWN</b><span>now</span></span>
          <b class="onb-notif-title">${escapeHtml(msg.title)}</b>
          <span class="onb-notif-body">${escapeHtml(msg.body)}</span>
        </span>
      </div>`;
  }

  function notifyHTML(): string {
    const q = `<h1 class="onb-q">Want a daily nudge?</h1>`;
    if (readiness === "install") {
      return `${q}
          <p class="onb-hint">One short, rude reminder a day with the days left. On iPhone that only works from the Home Screen app.</p>
          ${reminderPreview()}
          <ol class="install-steps onb-install">
            <li>Tap <span class="install-share">${SHARE_SVG} Share</span> in Safari's toolbar.</li>
            <li>Choose <b>Add to Home Screen</b>, then <b>Add</b>.</li>
            <li>Open it from the Home Screen. It starts fresh there, so set it up again and say yes to reminders.</li>
          </ol>`;
    }
    if (readiness === "denied") {
      return `${q}
          <p class="onb-hint">Notifications are switched off for C*UNTDOWN. Turn them on in your phone's Settings, under Notifications, then switch reminders on in the app's Settings.</p>
          ${reminderPreview()}`;
    }
    return `${q}
          <p class="onb-hint">One short, rude reminder a day with the days left, even with the app closed.</p>
          ${reminderPreview()}
          <label class="onb-hour">
            <span>Every day at</span>
            <select class="onb-input onb-hour-select" name="hour" aria-label="Reminder time">
              ${Array.from({ length: 24 }, (_, h) => `<option value="${h}"${h === draft.hour ? " selected" : ""}>${hourLabel(h)}</option>`).join("")}
            </select>
          </label>`;
  }

  // ---------- validation + live bits ----------

  function valid(): boolean {
    switch (step) {
      case "event":
        return draft.name.trim().length > 0;
      case "start":
        return Boolean(draft.start) && startInstant(draft).getTime() <= Date.now();
      case "end": {
        const end = endInstant(draft);
        return Boolean(end) && end!.getTime() > Date.now() && end!.getTime() > startInstant(draft).getTime();
      }
      default:
        return true;
    }
  }

  function refreshLive() {
    if (step === "person") {
      const greeting = root.querySelector<HTMLElement>('[data-el="greeting"]');
      if (greeting) {
        greeting.textContent = getDailyHeadline({ eventId: draft.id, personName: draft.personName, target: endInstant(draft) ?? addDays(new Date(), 30) });
      }
    }
    if (step === "end") {
      const readout = root.querySelector<HTMLElement>('[data-el="readout"]')!;
      const end = endInstant(draft);
      if (!end) {
        readout.textContent = "";
      } else if (end.getTime() <= Date.now()) {
        readout.textContent = "That's already been. Pick something after right now.";
        readout.dataset.tone = "bad";
      } else {
        const ms = end.getTime() - Date.now();
        const days = Math.floor(ms / 86_400_000);
        readout.textContent =
          days >= 1 ? `That's ${fmt(days)} ${days === 1 ? "day" : "days"} away. Or ${fmt(Math.floor(ms / 3_600_000))} hours, if you're counting.` : `That's later today. Tight, but fine.`;
        readout.dataset.tone = "good";
      }
    }
    nextBtn.disabled = !valid();
  }

  // ---------- rendering a step ----------

  function paint(direction: 1 | -1 | 0) {
    const qi = QUESTIONS.indexOf(step);
    const answered = Math.max(0, qi);
    progress.querySelectorAll("i").forEach((bar, i) => {
      bar.classList.toggle("is-done", i < answered);
      bar.classList.toggle("is-now", i === qi);
    });
    count.textContent = qi >= 0 ? `${qi + 1} of ${QUESTIONS.length}` : "";
    backBtn.hidden = step === "intro";
    root.firstElementChild!.classList.toggle("is-intro", step === "intro");
    stage.classList.toggle("is-intro", step === "intro");
    const asksForReminders = step === "notify" && (readiness === "default" || readiness === "granted");
    nextBtn.textContent =
      step === "intro" ? "Let's go" : asksForReminders ? "Remind me daily" : step === lastStep ? "Start the countdown" : "Next";
    skipBtn.hidden = step !== "person" && !asksForReminders;
    skipBtn.textContent = step === "person" ? "Skip, it's just me" : "Not now";

    stage.innerHTML = `<div class="onb-step">${stepHTML()}</div>`;
    const panel = stage.firstElementChild as HTMLElement;
    if (direction !== 0 && !reducedMotion) panel.classList.add(direction > 0 ? "from-right" : "from-left");
    refreshLive();
    saveDraft(draft, step);

    const input = stage.querySelector<HTMLInputElement>("#onb-input");
    if (input && input.type === "text") input.focus({ preventScroll: true });
    else if (direction !== 0) nextBtn.focus({ preventScroll: true });
  }

  function go(to: StepId, direction: 1 | -1) {
    step = to;
    paint(direction);
  }

  function next() {
    if (!valid()) {
      stage.firstElementChild?.classList.remove("shake");
      void (stage.firstElementChild as HTMLElement | null)?.offsetWidth;
      stage.firstElementChild?.classList.add("shake");
      return;
    }
    if (step === "notify" && (readiness === "default" || readiness === "granted")) return remindAndFinish();
    if (step === lastStep) return finish();
    go(STEPS[STEPS.indexOf(step) + 1], 1);
  }

  function back() {
    const i = STEPS.indexOf(step);
    if (i > 0) go(STEPS[i - 1], -1);
  }

  let finishing = false;
  let committed = false;
  function commit() {
    if (committed) return;
    committed = true;
    const end = endInstant(draft)!;
    store.saveEvent({
      id: draft.id,
      name: draft.name.trim(),
      personName: draft.personName.trim(),
      startDate: startInstant(draft).toISOString(),
      targetDate: end.toISOString(),
    });
    try {
      sessionStorage.removeItem(DRAFT_KEY);
    } catch {
      // nothing to clean up
    }
  }

  /**
   * Saves the countdown, asks for notification permission straight from the
   * tap (iOS only shows the prompt for one), then finishes. Registering with
   * the push server carries on in the background rather than holding up the
   * countdown; Settings shows how it went.
   */
  async function remindAndFinish() {
    if (finishing) return;
    commit();
    store.setNotificationPrefs({ hour: draft.hour });
    nextBtn.disabled = true;
    const permission = await requestNotificationPermission();
    if (permission === "granted") void enableReminders();
    else store.setNotificationPrefs({ enabled: false, lastPermission: permission === "unsupported" ? "unsupported" : permission });
    nextBtn.disabled = false;
    finish();
  }

  function finish() {
    if (finishing) return;
    finishing = true;
    commit();
    // Hand over to the countdown: it grows out of the button in a circle and
    // then plays its first-run entrance (the numbers count up to the real
    // thing), see ui/entrance.ts.
    markFirstRun();
    const r = nextBtn.getBoundingClientRect();
    const go = () => navigate("#/", { kind: "reveal", x: r.left + r.width / 2, y: r.top + r.height / 2 });
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return go();
    nextBtn.animate([{ scale: "1" }, { scale: ".93", offset: 0.4 }, { scale: "1.03" }], { duration: 220, easing: "ease-out" });
    window.setTimeout(go, 200);
  }

  // ---------- events ----------

  function onInput(e: Event) {
    const input = e.target as HTMLInputElement;
    if (!input.name) return;
    if (input.name === "hour") draft.hour = Number(input.value);
    else (draft as unknown as Record<string, string>)[input.name] = input.value;
    stage.querySelectorAll<HTMLElement>(`[data-chip-field="${input.name}"]`).forEach((chip) => {
      chip.classList.toggle("is-on", chip.dataset.chipValue === input.value);
    });
    refreshLive();
    saveDraft(draft, step);
  }

  function onStageClick(e: Event) {
    const chip = (e.target as HTMLElement).closest<HTMLElement>(".onb-chip");
    if (!chip) return;
    try {
      navigator.vibrate?.(8);
    } catch {
      // not on iOS
    }
    const field = chip.dataset.chipField!;
    const input = stage.querySelector<HTMLInputElement>(`[name="${field}"]`)!;
    input.value = chip.dataset.chipValue!;
    input.dispatchEvent(new Event("input", { bubbles: true }));
  }

  // Enter moves on from any field. Not left to implicit form submission,
  // which browsers skip when a step has two inputs (date + time).
  function onKeydown(e: KeyboardEvent) {
    if (e.key !== "Enter" || !(e.target instanceof HTMLInputElement)) return;
    e.preventDefault();
    next();
  }
  const onSubmit = (e: Event) => e.preventDefault();

  function onSkip() {
    if (step === "notify") return finish();
    draft.personName = "";
    go("start", 1);
  }

  stage.addEventListener("input", onInput);
  stage.addEventListener("click", onStageClick);
  stage.addEventListener("submit", onSubmit);
  stage.addEventListener("keydown", onKeydown);
  nextBtn.addEventListener("click", next);
  backBtn.addEventListener("click", back);
  skipBtn.addEventListener("click", onSkip);

  paint(0);

  return () => {
    stage.removeEventListener("input", onInput);
    stage.removeEventListener("click", onStageClick);
    stage.removeEventListener("submit", onSubmit);
    stage.removeEventListener("keydown", onKeydown);
    nextBtn.removeEventListener("click", next);
    backBtn.removeEventListener("click", back);
    skipBtn.removeEventListener("click", onSkip);
  };
}
